import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// App-native confirmation. Replaces every window.confirm/alert/prompt
// so destructive actions go through a single, consistent, themed
// dialog — and HIGH-severity actions can demand a typed token
// ("DELETE", a tenant slug, an email) before the confirm button
// unlocks. Imperative API so call sites read almost like the old
// `if (confirm(...))`:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title, description, destructive: true }))) return;

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for irreversible/destructive actions. */
  destructive?: boolean;
  /**
   * When set, the user must type this exact string before the confirm
   * button enables. Use for mass/irreversible actions (bulk delete,
   * offboard a tenant, anonymise a user).
   */
  confirmText?: string;
  /** Optional hint shown above the type-to-confirm input. */
  confirmTextLabel?: ReactNode;
}

type Resolver = (ok: boolean) => void;

const ConfirmContext = createContext<
  ((opts: ConfirmOptions) => Promise<boolean>) | null
>(null);

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within <ConfirmProvider>");
  }
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState("");
  const resolverRef = useRef<Resolver | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    // If a previous dialog is somehow still pending, resolve it false
    // so its caller isn't left hanging.
    resolverRef.current?.(false);
    setTyped("");
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    setTyped("");
    r?.(ok);
  }, []);

  const needsToken = !!opts?.confirmText;
  const tokenOk = !needsToken || typed.trim() === opts!.confirmText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={opts != null}
        onOpenChange={(open) => {
          // Backdrop click / Escape → treat as cancel.
          if (!open) settle(false);
        }}
      >
        {opts && (
          <AlertDialogContent
            onEscapeKeyDown={() => settle(false)}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{opts.title}</AlertDialogTitle>
              {opts.description != null && (
                <AlertDialogDescription asChild>
                  <div className="whitespace-pre-wrap">{opts.description}</div>
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>

            {needsToken && (
              <div className="space-y-1.5">
                <Label htmlFor="confirm-token" className="text-sm">
                  {opts.confirmTextLabel ?? (
                    <>
                      Type{" "}
                      <span className="font-mono font-semibold">
                        {opts.confirmText}
                      </span>{" "}
                      to confirm
                    </>
                  )}
                </Label>
                <Input
                  id="confirm-token"
                  autoFocus
                  autoComplete="off"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && tokenOk) settle(true);
                  }}
                  placeholder={opts.confirmText}
                />
              </div>
            )}

            <AlertDialogFooter>
              <Button variant="outline" onClick={() => settle(false)}>
                {opts.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                variant={opts.destructive ? "destructive" : "default"}
                disabled={!tokenOk}
                onClick={() => settle(true)}
              >
                {opts.confirmLabel ?? (opts.destructive ? "Delete" : "Confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
